import { createReferenceChartDatafeed } from "./referenceChartDatafeed";

export const REFERENCE_CHART_PAIRS = Object.freeze({
  "15M": [{ label: "15M", resolution: "15", seconds: 900 }, { label: "4H", resolution: "240", seconds: 14400 }],
  "4H": [{ label: "4H", resolution: "240", seconds: 14400 }, { label: "1D", resolution: "1D", seconds: 86400 }],
  "1D": [{ label: "1D", resolution: "1D", seconds: 86400 }, { label: "1D", resolution: "1D", seconds: 86400 }],
});
const libraryPath = `${process.env.PUBLIC_URL || ""}/tv_charts/charting_library/`;
let libraryPromise;

function loadLibrary() {
  if (window.TradingView?.widget) return Promise.resolve();
  if (!libraryPromise) libraryPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${libraryPath}charting_library.standalone.js`;
    script.onload = () => window.TradingView?.widget ? resolve() : reject(new Error("Chart library is unavailable."));
    script.onerror = () => reject(new Error("Chart library could not be loaded. Close the popup and try again."));
    document.head.appendChild(script);
  }).catch((error) => { libraryPromise = null; throw error; });
  return libraryPromise;
}

function cloneDrawingValue(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function exportUserDrawings(chart, marker, cutoffExclusive) {
  return chart.getAllShapes().filter(({ id }) => id !== marker).map(({ id, name }) => {
    const shape = chart.getShapeById(id);
    const anchoredPosition = shape.getAnchoredPosition?.();
    const points = anchoredPosition ? [] : shape.getPoints();
    if (!anchoredPosition && (!Array.isArray(points) || points.length === 0
        || points.some((point) => !Number.isFinite(point?.time) || point.time >= cutoffExclusive))) {
      return null;
    }
    return {
      name,
      anchoredPosition: cloneDrawingValue(anchoredPosition),
      points: cloneDrawingValue(points),
      properties: cloneDrawingValue(shape.getProperties()),
    };
  }).filter(Boolean);
}

function importUserDrawings(chart, drawings) {
  for (const drawing of drawings || []) {
    const options = { shape: drawing.name, disableSave: true, disableUndo: true };
    const id = drawing.anchoredPosition
      ? chart.createAnchoredShape(drawing.anchoredPosition, options)
      : drawing.points.length === 1
        ? chart.createShape(drawing.points[0], options)
        : chart.createMultipointShape(drawing.points, options);
    if (id == null) throw new Error(`Could not reproduce the ${drawing.name} reference drawing.`);
    const shape = chart.getShapeById(id);
    shape.setProperties(drawing.properties || {});
    shape.setSavingEnabled(false);
    shape.setSelectionEnabled(false);
    shape.setUserEditEnabled(false);
  }
}

export function candleTime(time, spec) {
  const offset = spec.resolution === "1W" ? 345600 : 0; // Binance weeks open Monday UTC.
  return Math.floor((time - offset) / spec.seconds) * spec.seconds + offset;
}

export function deadline(promise, message, milliseconds = 30000, signal) {
  let timer;
  let onAbort;
  return Promise.race([promise, new Promise((_, reject) => {
    onAbort = () => reject(new Error("Chart capture cancelled."));
    timer = setTimeout(() => reject(new Error(message)), milliseconds);
    if (signal?.aborted) onAbort();
    else signal?.addEventListener("abort", onAbort, { once: true });
  })]).finally(() => { clearTimeout(timer); signal?.removeEventListener("abort", onAbort); });
}

export const painted = (signal) => new Promise((resolve, reject) => {
  let frame;
  const onAbort = () => { cancelAnimationFrame(frame); reject(new Error("Chart capture cancelled.")); };
  if (signal?.aborted) { onAbort(); return; }
  signal?.addEventListener("abort", onAbort, { once: true });
  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => { signal?.removeEventListener("abort", onAbort); resolve(); });
  });
});

async function waitForIndicators(chart, studies, sampleTime, signal, disposed) {
  const expiresAt = Date.now() + 30000;
  let clearSince = null;
  const message = "Capture could not finish: indicator values are not ready at the selected candle. Wait a moment and try Capture setup and outcome again.";
  while (Date.now() < expiresAt) {
    if (disposed() || signal?.aborted) throw new Error("Chart loading cancelled.");
    let valuesReady = false;
    try {
      // Legends can be painted on canvas; inspect the calculated study data instead.
      const exported = await deadline(chart.exportData({
        from: sampleTime, to: sampleTime, includeTime: true, includeSeries: false,
        includedStudies: studies, includeDisplayedValues: false,
      }), message, Math.max(1, expiresAt - Date.now()), signal);
      const timeIndex = exported.schema.findIndex((field) => field.type === "time");
      const row = exported.data.find((entry) => entry[timeIndex] === sampleTime);
      valuesReady = !!row && studies.every((id) => exported.schema.some((field, index) =>
        field.sourceType === "study" && field.sourceId === id && Number.isFinite(row[index])));
    } catch (_) { /* Export can be unavailable while the studies are calculating. */ }
    if (valuesReady) {
      if (clearSince == null) clearSince = Date.now();
      if (Date.now() - clearSince >= 300) return;
    } else clearSince = null;
    await deadline(new Promise((resolve) => setTimeout(resolve, 100)), message, 1000, signal);
  }
  throw new Error(message);
}

export async function openReferenceChart(container, symbol, spec, signal, onRange, onSelect, options = {}) {
  const wait = (promise, message) => deadline(promise, message, 30000, signal);
  await wait(loadLibrary(), "Chart library timed out.");
  if (signal.aborted) throw new Error("Chart loading cancelled.");
  const feed = createReferenceChartDatafeed(symbol, {
    cutoffExclusive: options.cutoffTime, partialBaseResolution: options.partialBaseResolution,
  });
  let widget;
  let marker;
  let rangeSubscription;
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    signal.removeEventListener("abort", dispose);
    feed.dispose();
    try { rangeSubscription?.unsubscribe(null, onRange); } catch (_) { /* Already removed. */ }
    try { widget?.remove(); } catch (_) { /* Already removed. */ }
  };
  signal.addEventListener("abort", dispose, { once: true });
  try {
    widget = new window.TradingView.widget({
      container, symbol, interval: spec.resolution, datafeed: feed.datafeed,
      library_path: libraryPath, theme: "dark", timezone: "Etc/UTC", locale: "en",
      width: "100%", height: 540, allow_symbol_change: false,
      disabled_features: ["header_widget", "timeframes_toolbar", "use_localstorage_for_settings", "symbol_search_hot_key"],
      enabled_features: ["volume_force_overlay", "create_volume_indicator_by_default"],
      overrides: { "paneProperties.background": "#131722", "paneProperties.backgroundType": "solid" },
      studies_overrides: { "moving average.length": 99, "moving average.plot.color": "#66CC66", "bollinger bands.length": 20 },
    });
    await wait(new Promise((resolve) => widget.onChartReady(resolve)), "Chart loading timed out. Check the symbol and connection.");
    if (signal.aborted) throw new Error("Chart loading cancelled.");
    const chart = widget.activeChart();
    const studies = await wait(Promise.all([
      chart.createStudy("Moving Average", false, true), chart.createStudy("Bollinger Bands", false, true),
      chart.createStudy("MACD", false, true), chart.createStudy("Relative Strength Index", false, true),
    ]), "Chart indicators could not be loaded.");
    if (studies.some((id) => id == null)) throw new Error("A required chart indicator is unavailable.");
    if (signal.aborted) throw new Error("Chart loading cancelled.");
    const ready = async (readySignal = signal) => {
      if (disposed || signal.aborted) return Promise.reject(new Error("Chart loading cancelled."));
      await deadline(new Promise((resolve) => { if (chart.dataReady(resolve)) resolve(); }), "Chart candles did not finish loading.", 30000, readySignal);
    };
    await ready();
    if (signal.aborted) throw new Error("Chart loading cancelled.");
    if (onRange) { rangeSubscription = chart.onVisibleRangeChanged(); rangeSubscription.subscribe(null, onRange); }
    widget.onContextMenu((time) => onSelect && Number.isFinite(time)
      ? [{ text: "Select reference candle", position: "top", click: () => onSelect(candleTime(time, spec)) }] : []);
    return {
      spec, chart, widget, ready,
      getWidth: () => container.getBoundingClientRect().width,
      async captureReady(sampleTime, captureSignal = signal) {
        await ready(captureSignal);
        await waitForIndicators(chart, studies, candleTime(sampleTime, spec), captureSignal, () => disposed);
      },
      hasCandle: (time) => feed.hasCandle(time, spec.resolution),
      showsCandle(time) {
        const range = chart.getVisibleRange();
        const open = candleTime(time, spec);
        return range && open >= range.from && open <= range.to;
      },
      async center(time, preserveSpan = true) {
        const range = chart.getVisibleRange();
        const span = preserveSpan && range?.to > range?.from ? range.to - range.from : 120 * spec.seconds;
        await wait(chart.setVisibleRange({ from: time - span / 2, to: time + span / 2 }), "Could not scroll to this candle.");
        await ready();
      },
      async mark(time, text = "Reference candle") {
        if (disposed || signal.aborted) throw new Error("Chart loading cancelled.");
        if (marker != null) { chart.removeEntity(marker); marker = null; }
        marker = await wait(Promise.resolve(chart.createShape({ time: candleTime(time, spec) }, {
          shape: "vertical_line", text, lock: true, disableSelection: true, disableSave: true,
          overrides: { linecolor: "#22d3ee", linewidth: 2, linestyle: 2, showLabel: text !== "Reference candle" },
        })), "Could not mark the reference candle.");
        if (marker == null) throw new Error("Could not mark the reference candle.");
      },
      exportUserDrawings: (cutoffExclusive) => exportUserDrawings(chart, marker, cutoffExclusive),
      importUserDrawings: (drawings) => importUserDrawings(chart, drawings),
      dispose,
    };
  } catch (error) { dispose(); throw error; }
}

export async function composeReferenceCapture(charts, symbol, time, slot, options = {}) {
  const { signal, sampleTimes = charts.map(() => time), header, headers } = options;
  const check = () => {
    if (signal?.aborted) throw new Error("Chart capture cancelled.");
    if (sampleTimes.length !== charts.length || !charts.every((chart, index) => Number.isFinite(sampleTimes[index])
        && chart.hasCandle(sampleTimes[index]) && chart.showsCandle(sampleTimes[index]))) {
      throw new Error("The required candle is not visible on both charts. Go to the candle again before capture.");
    }
  };
  await deadline(Promise.all(charts.map((chart) => chart.ready(signal))), "Chart candles did not finish loading.", 30000, signal);
  check();
  // Only capture waits for calculated studies; loading and navigation stay interactive.
  await Promise.all(charts.map((chart, index) => chart.captureReady(sampleTimes[index], signal)));
  check();
  await deadline(painted(signal), "Chart rendering timed out.", 30000, signal);
  check();
  const ranges = charts.map((chart) => JSON.stringify(chart.chart.getVisibleRange()));
  const images = await deadline(Promise.all(charts.map((chart) => chart.widget.takeClientScreenshot())), "Chart screenshot timed out.", 30000, signal);
  check();
  if (charts.some((chart, index) => JSON.stringify(chart.chart.getVisibleRange()) !== ranges[index])) {
    throw new Error("A chart moved during capture. Wait for it to settle and capture again.");
  }
  if (images.some((image) => !image.width || !image.height)) throw new Error("Chart screenshot is empty.");
  const headerHeight = 40;
  const width = Math.max(...images.map((image) => image.width));
  const height = images.reduce((total, image) => total + headerHeight + image.height, 0);
  if (width > 4096 || height > 4096 || width * height > 4_000_000) {
    throw new Error("The original-size charts exceed the image dimensions limit. Use a narrower chart window and capture again.");
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Your browser cannot create chart screenshots.");
  ctx.fillStyle = "#131722"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  let top = 0;
  images.forEach((image, index) => {
    ctx.fillStyle = "#e2e8f0"; ctx.font = "18px sans-serif";
    ctx.fillText(`${symbol} · ${charts[index].spec.label} · ${slot} · ${headers?.[index] || header || new Date(time * 1000).toISOString().replace('.000Z', ' UTC')}`, 16, top + 26, width - 32);
    // Copy native pixels at integer coordinates: no scaling or interpolation.
    ctx.drawImage(image, Math.floor((width - image.width) / 2), top + headerHeight);
    top += headerHeight + image.height;
  });
  const dataUrl = canvas.toDataURL("image/png");
  const encoded = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bytes = encoded.length * 3 / 4 - (encoded.endsWith("==") ? 2 : encoded.endsWith("=") ? 1 : 0);
  if (dataUrl.startsWith("data:image/png;base64,") && bytes <= 2 * 1024 * 1024) return dataUrl;
  throw new Error("The lossless screenshot exceeds the 2 MiB upload limit. Use a narrower chart window and capture again.");
}
